<template>
  <div class="flex flex-col gap-1 mb-4">
    <div class="font-bold">
      Call Recording{{ recordings.length > 1 ? "s" : "" }}
    </div>
    <div v-if="playable" class="flex flex-col gap-4 mt-1">
      <Alert
        v-if="dialpadUnrecorded"
        type="info"
        class="call-recording-off-notice"
        data-cy="call-recording-off-notice"
      >
        <span class="d-block text-sm font-medium text-gray-900">
          Dialpad didn't record this call
        </span>
        <span class="d-block mt-1 text-xs text-gray-700">
          Dialpad only saves a recording when the call is recorded. Turn on
          automatic call recording in Dialpad so every call has one.
        </span>
        <button
          type="button"
          class="link d-block mt-1 p-0 text-xs bg-transparent border-0"
          data-cy="call-recording-off-help"
          @click="helpTopic = 'recording'"
        >
          Where to find it in Dialpad
        </button>
      </Alert>
      <DialpadHelpDrawer
        v-if="helpTopic"
        :topic="helpTopic"
        @close="helpTopic = null"
      />
      <div
        v-for="recording in recordings"
        :key="recording.id"
        class="flex flex-col gap-2"
        data-cy="call-recording"
      >
        <audio
          v-if="recording.stream_url"
          class="w-full"
          controls
          preload="none"
          aria-label="Call recording"
          :src="recording.stream_url"
          data-cy="call-recording-player"
          @error="emit('playback-error')"
        ></audio>
        <div
          v-else-if="recording.playback_url"
          class="inline-flex cursor-pointer"
          @click="handleRecordingClick(recording)"
        >
          <div class="playback-link text-primary-700">
            <i class="fa-solid fa-circle-play mr-1"></i>
            Listen to Recording
          </div>
        </div>
        <CallTranscript
          v-if="recording.transcriptParsed?.length"
          :lines="recording.transcriptParsed"
        />
        <!-- The notice below speaks for a Dialpad call with no transcript. -->
        <div v-else-if="!dialpadUntranscribed">--</div>
      </div>
      <!-- Below the recording, where its transcript will appear. -->
      <Alert
        v-if="dialpadUntranscribed"
        type="info"
        class="call-recording-off-notice"
        data-cy="call-transcript-off-notice"
      >
        <span class="d-block text-sm font-medium text-gray-900">
          No transcript for this call yet
        </span>
        <span class="d-block mt-1 text-xs text-gray-700">
          A call's transcript shows within 2-3 minutes after it ends. If you
          don't see one by then, turn on Dialpad AI.
        </span>
        <button
          type="button"
          class="link d-block mt-1 p-0 text-xs bg-transparent border-0"
          data-cy="call-transcript-off-help"
          @click="helpTopic = 'transcript'"
        >
          Where to find it in Dialpad
        </button>
      </Alert>
      <div
        v-if="!recordings.length && !dialpadUnrecorded"
        data-cy="call-recording-none"
      >
        --
      </div>
    </div>
    <div v-else class="transcript p-2 mt-1 flex-col gap-2">
      <div v-for="(recording, idx) in recordings" :key="recording.id">
        <div v-if="recording.transcriptParsed?.length" class="w-full">
          <div
            v-for="(line, index) in recording.transcriptParsed"
            :key="index"
            class="line w-full"
            :class="recording.transcriptParsed[0]?.user"
          >
            <div>
              <strong>{{ line.user }}: </strong><span>{{ line.message }}</span>
            </div>
          </div>
        </div>
        <div v-else>--</div>
        <div
          v-if="recording.stream_url || recording.playback_url"
          class="inline-flex cursor-pointer mt-2"
          @click="handleRecordingClick(recording)"
        >
          <div class="playback-link text-primary-700">
            <i class="fa-solid fa-circle-play mr-1"></i>
            Listen to Recording
          </div>
        </div>
        <hr
          v-if="idx < recordings.length - 1"
          class="my-4 border-b-none border-gray-200"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import chunk from "lodash/chunk";
import API from "serviceshift-ui/api-client";
import { computed, ref } from "vue";
import { getProperty } from "@/lib/helpers";

import Alert from "@/components/Seranova/Alert/Alert.vue";
import DialpadHelpDrawer, {
  type DialpadHelpTopic
} from "@/Marketplace/DialpadHelpDrawer.vue";
import {
  CallShow,
  RecordingsEntity,
  TranscriptParsedEntity
} from "@/typings/call";
import CallTranscript from "./CallTranscript.vue";

const props = withDefaults(
  defineProps<{
    call: CallShow;
    // Plays recordings in the page and makes the transcript searchable.
    playable?: boolean;
  }>(),
  { playable: false }
);
// A signed link that has expired or failed; the parent fetches fresh links.
const emit = defineEmits<{ (e: "playback-error"): void }>();

// Statuses of a call that was answered and has ended, so Dialpad would have sent
// its recording, and Dialpad AI written its transcript, if it had made one.
const ENDED_ANSWERED_STATUSES = ["completed", "abandoned"];

const dialpadAnsweredAndEnded = computed(
  () =>
    props.call.source_name === "dialpad" &&
    props.call.answered &&
    ENDED_ANSWERED_STATUSES.includes(props.call.status ?? "")
);

const dialpadUnrecorded = computed(
  () =>
    dialpadAnsweredAndEnded.value &&
    !recordings.value.some((recording) => recording.stream_url)
);

// Dialpad AI writes a finished call's transcript, which Sera fetches a couple of
// minutes later. A voicemail's comes with the voicemail, so it never lands here.
const dialpadUntranscribed = computed(
  () =>
    dialpadAnsweredAndEnded.value &&
    !recordings.value.some((recording) => recording.transcriptParsed?.length)
);

const helpTopic = ref<DialpadHelpTopic | null>(null);

const recordings = computed<RecordingsEntity[]>(() => {
  if (!props.call.recordings?.length && !props.call.playback_url) return [];

  return (
    props.call.recordings || [
      {
        id: Date.now(),
        playback_url: props.call.playback_url || "",
        transcript: props.call.transcript || ""
      } as RecordingsEntity
    ]
  ).map((recording) => ({
    ...recording,
    transcriptParsed: parseTranscript(recording.transcript)
  }));
});

const handleRecordingClick = async (recording: any) => {
  // A Dialpad recording has no provider URL; its signed Sera link plays it instead.
  if (recording.stream_url) {
    window.open(recording.stream_url, "_blank");
    return;
  }
  if (props.call.links.get_source_access_token) {
    const token = await API.makeRequest(
      props.call.links.get_source_access_token,
      {
        method: "GET"
      }
    ).then((res) => getProperty(res, "data.access_token", ""));
    const url = new URL(recording.playback_url);
    url.searchParams.append("access_token", token);
    window.open(url.toString(), "_blank");
    return;
  }
  window.open(recording.playback_url, "_blank");
};

const parseTranscript = (transcript): TranscriptParsedEntity[] => {
  if (!transcript) return [];
  const transcriptMessages = transcript
    .split(new RegExp(`(Agent|Caller): `))
    .filter((message) => !!message);

  // Just in case this is not a conversation (ex: voicemail), return the message from the caller
  if (transcriptMessages.length === 1) {
    return [
      {
        user: "Caller",
        message: transcript
      }
    ];
  }
  return chunk(transcriptMessages, 2).map((row) => ({
    user: row[0] as string,
    message: row[1] as string
  }));
};
</script>

<style lang="scss" scoped>
.transcript {
  background: #fff;
  box-shadow: var(--box-shadow);
  border-radius: 4px;
  overflow: auto;
  max-height: 450px;
  .line {
    margin-bottom: 12px;
    &:last-of-type {
      margin-bottom: 0;
    }
  }
}

// The info badge pinned to the first line, as on the Dialpad Marketplace card.
// Tailwind runs with important: true here.
.call-recording-off-notice {
  align-items: flex-start !important;

  :deep(> svg) {
    align-self: flex-start !important;
    margin-top: 0.0625rem;
    flex-shrink: 0;
  }
}
</style>
